import React, { useState } from 'react';

import { Card, useDevice } from '@tecma/ds';
import { useTranslation } from '@tecma/i18n';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import useCarousel from 'hooks/useCarousel';
import { useQuoteDetailsModal } from 'hooks/useQuoteDetailsModal';
import { useProjectAssetType } from 'hooks/useProjectAssetType';

import { applicationStateSelectors } from 'store/slices/applicationState/selectors';

import { CardQuoteContainer } from 'components/CardQuoteContainer';
import { ContactUsModal } from 'components/ContactUsModal';
import { HighlightedContent } from 'components/HighlightedContent';
import { QuoteDetailsModal } from 'components/QuoteDetailsModal';
import { SendRequestModal } from 'components/SendRequestModal/SendRequestModal';

import { redirectToConfigurator } from 'utils/redirectToConfigurator';
import { getBannerProps, getRoomBannerProps } from 'utils/getBannerProps';
import { getPlanImage } from 'utils/getPlanImage';
import { formatDate } from 'utils/formatDates';
import { truncateTo1Decimal } from 'utils/formatNumbers';

import { configurationsActions } from 'store/sagas/configurations/configurations.actions';
import { SELECTED_QUOTE_STATUSES } from 'constants/quoteStatus';
import { CardApartmentCarousel } from './CardApartmentCarousel';
import { CardApartmentInfo } from './CardApartmentInfo';

import type { Building, SurfaceArea } from 'types/configurations';
import type { Quote, QuoteStatus } from 'types/quote';

import './card-apartment.scss';

export type ApartmentFeatures = {
  rooms?: number;
  bathroom?: number;
  privateBathroom?: boolean;
  bedroom?: number;
  openPlanKitchen?: boolean;
  floor?: number;
  buildingName?: string;
  side?: string;
  surface?: number;
};

interface CardApartmentRequiredProps {
  id: string;
  name: string;
  typologyName: string;
  floor: number;
  building: Building;
  images: string[];
  features: ApartmentFeatures;
  surfaceArea?: SurfaceArea;
  quotes: Quote[];
  status: QuoteStatus;
  code?: string;
}

// Optional Props
interface CardApartmentOptionalProps {
  planName?: string;
  planNameRoom?: string;
  apartmentData?: CardApartmentProps;
}

// Combined required and optional props to build the full prop interface
export interface CardApartmentProps extends CardApartmentRequiredProps, CardApartmentOptionalProps {}

const CardApartment: React.FC<CardApartmentProps> = ({
  id,
  name,
  typologyName,
  building,
  images,
  features,
  quotes,
  status,
  planName,
  planNameRoom,
  code,
}) => {
  const { t } = useTranslation();
  const device = useDevice();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isContactUsModalOpen, setIsContactUsModalOpen] = useState(false);

  const projectAssetType = useProjectAssetType();
  const area = projectAssetType.area;
  const experienceType = projectAssetType.experienceType;
  const displayName = useSelector(applicationStateSelectors.selectDisplayName);
  const assetTypology = projectAssetType.assetTypology;
  const spaceConfigurator = useSelector(applicationStateSelectors.selectSpaceConfigurator);
  const coliving = useSelector(applicationStateSelectors.selectColiving);

  const planImage = getPlanImage({
    area: area!,
    displayName: displayName!,
    planName: assetTypology === 'room' ? planNameRoom : planName,
    building,
    assetTypology,
  });

  const carouselProps = useCarousel([planImage, ...images].length);
  const {
    quoteList,
    handleOnClose,
    isQuoteDetailsModalOpen,
    toggleQuoteDetailsModal,
    getModalTitle,
    selectedQuote,
    getModalDescription,
    isSendRequestModalOpen,
    setIsSendRequestModalOpen,
    handleSendRequest,
  } = useQuoteDetailsModal({
    quotes,
    apartmentName: name,
    isSendRequestFlow: true,
    space: {
      code,
      name,
      building,
    },
  });

  const isRoomAssetType = assetTypology === 'room';
  const { rooms, floor, surface, privateBathroom } = features;
  const formattedSurface = truncateTo1Decimal(surface);
  const isDesktop = device.type === 'desktop';
  const isQuoteSelected = SELECTED_QUOTE_STATUSES.includes(status);

  const navigateTo = () => navigate(`/apartments/${id}`);
  const toggleContactUsModal = () => {
    setIsContactUsModalOpen(!isContactUsModalOpen);
  };

  const refreshConfigurations = () => {
    dispatch(configurationsActions.initConfigurations());
  };

  const bannerProps = isRoomAssetType
    ? getRoomBannerProps({
        t,
        sendRequestAction: toggleQuoteDetailsModal,
        moreDetailsAction: navigateTo,
        contactUsAction: toggleContactUsModal,
        createConfigurationAction: () => redirectToConfigurator({ assetType: assetTypology, spaceConfigurator, coliving }),
        checkInDate: quotes[0]?.dateRange?.checkInDate ? formatDate(new Date(quotes[0]?.dateRange?.checkInDate)) : '',
      })
    : getBannerProps({
        t,
        firstButtonAction: toggleContactUsModal,
        secondButtonAction: navigateTo,
        thirdButtonAction: () => redirectToConfigurator({ assetType: assetTypology, spaceConfigurator, coliving }),
        sendRequestAction: toggleQuoteDetailsModal,
        contactUsAction: toggleContactUsModal,
        checkInDate: quotes[0]?.dateRange?.checkInDate ? formatDate(new Date(quotes[0]?.dateRange?.checkInDate)) : '',
        area,
      });
  const currentBanner = bannerProps?.[status];

  const roomBathroom = privateBathroom
    ? {
        privateBathroom: true,
      }
    : {
        sharedBathroom: true,
      };
  const linkedQuoteTargetId = quotes.find((quote) => quote.oldRequest)?.id ?? quotes[0]?.id;
  const cardApartmentHeaderId = linkedQuoteTargetId ? `card-apartment-${linkedQuoteTargetId}` : undefined;
  const linkedQuoteIds = quotes.map((quote) => quote.id).join(' ');

  return (
    <>
      <ContactUsModal isOpen={isContactUsModalOpen} toggleModal={toggleContactUsModal} showBanner={status === 'init'} />
      <QuoteDetailsModal
        isOpen={isQuoteDetailsModalOpen}
        onClose={handleOnClose}
        getModalTitle={getModalTitle}
        quoteList={quoteList}
        selectedQuote={selectedQuote}
        getModalDescription={getModalDescription}
        isSendRequestFlow
        footerActionOnClose={handleOnClose}
        footerActionOnSubmit={() => setIsSendRequestModalOpen(true)}
        onQuoteDeleted={refreshConfigurations}
      />
      {!!selectedQuote && (
        <SendRequestModal
          isOpen={isSendRequestModalOpen}
          onClose={() => setIsSendRequestModalOpen(false)}
          quote={selectedQuote}
          onSubmit={handleSendRequest}
        />
      )}

      <Card className='card-apartment' borderLess data-linked-quote-ids={linkedQuoteIds}>
        <div id={cardApartmentHeaderId} className='card-apartment-header'>
          {/* {(isQuoteSelected || INVALID_STATUSES.includes(status)) && !isDesktop && (
            <ApartmentStatusTag
              color={INVALID_STATUSES.includes(status) ? 'error' : 'primary'}
              label={isQuoteSelected ? 'your-apartment' : 'unavailable'}
            />
          )} */}
          <CardApartmentCarousel images={[planImage, ...images]} {...carouselProps} />
          <CardApartmentInfo
            name={name}
            typologyName={typologyName}
            building={building}
            features={{ ...(experienceType === 'coliving' ? roomBathroom : {}), rooms, floor, surface: formattedSurface }}
            isQuoteSelected={isQuoteSelected}
          />
          {isDesktop && currentBanner && <HighlightedContent {...currentBanner} />}
        </div>
        {!isQuoteSelected && !!quotes.length && (
          <CardQuoteContainer
            quotes={quotes}
            label={t('quote.your-configurations')}
            apartmentName={`${t(`apartment-model.${typologyName}`, { defaultValue: typologyName })} ${name}`}
            showSidebar={isDesktop}
            onQuoteDeleted={refreshConfigurations}
          />
        )}
        {!isDesktop && currentBanner && <HighlightedContent {...currentBanner} horizontal={device.type === 'tablet'} />}
      </Card>
    </>
  );
};

export default React.memo(CardApartment);
